import { useEffect, useState } from 'react';
import { History, IdCard, ShieldCheck, UserCircle, UploadCloud, LogOut, Trash2 } from 'lucide-react';
import { ApiError, api } from '../services/apiClient';
import { useAuth } from '../features/auth/AuthContext';
import { useAccountCredits } from '../features/account/useAccountCredits';
import { Upload } from './Upload';
import { useNavigate } from 'react-router-dom';
import type { AccountCreditsDto } from '@primetime/shared/apiContracts';

type Tab = 'profile' | 'membership' | 'verification' | 'billing' | 'upload' | 'logout';

export function Account() {
  const { user, loading: authLoading, logout, clearSession } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('profile');
  const [payments, setPayments] = useState<any[]>([]);
  const { creditState } = useAccountCredits(user?.id, authLoading);
  const credits = creditState.data;
  const creditsLoading = creditState.status === 'loading';
  const creditsError = creditState.error;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteState, setDeleteState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    api<any[]>('/api/account/payments').then(setPayments).catch(() => setPayments([]));
  }, []);

  const items = [
    { id: 'profile' as Tab, icon: UserCircle, label: 'My Profile' },
    { id: 'membership' as Tab, icon: IdCard, label: 'Membership Status' },
    { id: 'verification' as Tab, icon: ShieldCheck, label: 'DJ Verification' },
    { id: 'billing' as Tab, icon: History, label: 'Billing & Settings' },
    { id: 'upload' as Tab, icon: UploadCloud, label: 'Upload Edits' },
    { id: 'logout' as Tab, icon: LogOut, label: 'Logout' }
  ];

  async function handleTabClick(id: Tab) {
    if (id === 'logout') {
      await logout();
      navigate('/');
    } else {
      setTab(id);
    }
  }

  async function handleDeleteAccount() {
    setDeleteError('');
    if (deleteConfirmation !== 'DELETE') {
      setDeleteState('error');
      setDeleteError('Type DELETE to confirm account deletion.');
      return;
    }

    setDeleteState('loading');
    try {
      await api<{ ok: true }>('/api/account/delete', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: deletePassword, confirmation: deleteConfirmation })
      });
      clearSession();
      navigate('/login', {
        replace: true,
        state: { message: 'Your account has been deleted.' }
      });
    } catch (error) {
      setDeleteState('error');
      setDeleteError(error instanceof ApiError ? error.message : 'Unable to delete account. Please try again.');
    }
  }

  return (
    <section className='container-shell py-14'>
      <h1 className='mb-8 text-4xl font-black'>MY ACCOUNT</h1>
      <div className='grid gap-6 lg:grid-cols-[280px_1fr]'>
        <aside className='glass h-fit rounded-2xl p-4'>
          {items.map(item => (
            <button key={item.id} onClick={() => handleTabClick(item.id)} className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left font-bold transition ${tab === item.id ? 'bg-prime-red text-white' : 'text-white/65 hover:bg-white/10'}`}>
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </aside>
        <main className='glass rounded-2xl p-8'>
          {tab === 'profile' && (
            <>
              <h2 className='text-2xl font-black'>My Profile</h2>
              <p className='mt-2 text-sm text-white/45'>Account details are read-only for now.</p>
              <div className='mt-8 grid gap-6 md:grid-cols-2'>
                <Info label='Email Address' value={user?.email || '-'} />
                <Info label='User ID' value={user?.id || '-'} />
                <Info label='DJ Name' value={user?.stageName || '-'} />
                <Info label='City' value={user?.city || '-'} />
                {user?.socialProfile && <Info label='Social Profile' value={user.socialProfile} />}
              </div>
              <section className='mt-10 border-t border-prime-red/30 pt-8'>
                <div className='flex items-start gap-3'>
                  <div className='rounded-xl border border-prime-red/40 bg-prime-red/10 p-3 text-prime-red'>
                    <Trash2 size={20} />
                  </div>
                  <div className='min-w-0 flex-1'>
                    <h3 className='text-xl font-black text-prime-red'>Danger Zone</h3>
                    <p className='mt-2 text-sm leading-6 text-white/55'>
                      Delete account removes your access, signs you out, and is permanent from your point of view.
                    </p>
                    {!deleteOpen && (
                      <button className='mt-5 rounded-xl border border-prime-red/60 px-5 py-3 text-sm font-black text-prime-red transition hover:bg-prime-red hover:text-white' type='button' onClick={() => setDeleteOpen(true)}>
                        Delete account
                      </button>
                    )}
                  </div>
                </div>

                {deleteOpen && (
                  <div className='mt-6 rounded-xl border border-prime-red/35 bg-black/35 p-5'>
                    <h4 className='font-black text-white'>Delete account</h4>
                    <p className='mt-2 text-sm leading-6 text-white/55'>
                      Enter your current password and type DELETE to confirm. This action cannot be undone from your account.
                    </p>
                    {deleteState === 'error' && deleteError && (
                      <div className='mt-4 rounded-xl border border-prime-red/40 bg-prime-red/10 p-3 text-sm text-red-200'>
                        {deleteError}
                      </div>
                    )}
                    <label className='mt-5 block text-sm font-bold text-white/70'>Current password</label>
                    <input
                      className='form-input mt-2'
                      type='password'
                      value={deletePassword}
                      autoComplete='current-password'
                      onChange={(event) => setDeletePassword(event.target.value)}
                    />
                    <label className='mt-4 block text-sm font-bold text-white/70'>Type DELETE</label>
                    <input
                      className='form-input mt-2'
                      value={deleteConfirmation}
                      autoComplete='off'
                      onChange={(event) => setDeleteConfirmation(event.target.value)}
                    />
                    <div className='mt-5 flex flex-wrap gap-3'>
                      <button
                        className='rounded-xl bg-prime-red px-5 py-3 text-sm font-black text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-45'
                        type='button'
                        disabled={!deletePassword || deleteConfirmation !== 'DELETE' || deleteState === 'loading'}
                        onClick={handleDeleteAccount}
                      >
                        {deleteState === 'loading' ? 'Deleting...' : 'Delete account'}
                      </button>
                      <button
                        className='rounded-xl border border-white/15 px-5 py-3 text-sm font-black text-white/70 transition hover:bg-white/10'
                        type='button'
                        disabled={deleteState === 'loading'}
                        onClick={() => {
                          setDeleteOpen(false);
                          setDeletePassword('');
                          setDeleteConfirmation('');
                          setDeleteError('');
                          setDeleteState('idle');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </>
          )}
          {tab === 'membership' && (
            <>
              <h2 className='text-2xl font-black'>Membership Status</h2>
              <p className='mt-2 text-sm text-white/45'>PrimeTime Credits are updated automatically.</p>
              {creditsLoading ? (
                <p className='mt-8 rounded-xl border border-white/10 bg-black/30 p-6 text-lg font-bold text-white/70'>Loading membership status...</p>
              ) : creditsError ? (
                <p className='mt-8 rounded-xl border border-prime-red/30 bg-prime-red/10 p-6 text-sm font-bold text-red-100'>{creditsError}</p>
              ) : (
                <MembershipSummary credits={credits} user={user} />
              )}
            </>
          )}
          {tab === 'verification' && (
            <>
              <h2 className='text-2xl font-black'>DJ Verification</h2>
              <p className='mt-2 text-sm text-white/45'>Verification details will be managed here later.</p>
              <div className='mt-8 grid gap-6 md:grid-cols-2'>
                <Info label='Current Status' value='Coming Soon' />
                <Info label='Account Role' value={user?.role || '-'} />
                <Info label='Stage Name' value={user?.stageName || '-'} />
              </div>
            </>
          )}
          {tab === 'billing' && (
            <>
              <h2 className='text-2xl font-black'>Billing & Settings</h2>
              <p className='mt-2 text-sm text-white/45'>Receipts, billing preferences, and account settings will live here later.</p>
              <Table rows={payments} empty='No billing history available.' />
            </>
          )}
          {tab === 'upload' && (
            <Upload />
          )}
        </main>
      </div>
    </section>
  );
}

function Info({ label, value, highlight }: { label: string; value: string; highlight?: 'green' | 'red' }) {
  return (
    <div className='rounded-xl border border-white/10 bg-black/30 p-4'>
      <p className='text-xs font-black uppercase tracking-[0.18em] text-white/35'>{label}</p>
      <p className={`mt-2 font-bold ${highlight === 'green' ? 'text-emerald-400' : highlight === 'red' ? 'text-prime-red' : 'text-white'}`}>{value}</p>
    </div>
  );
}

function MembershipSummary({ credits, user }: { credits: AccountCreditsDto | null; user: any }) {
  const hasActiveMembership = credits?.hasActiveMembership ?? user?.hasActiveMembership ?? false;
  const planName = credits?.activePlanName || credits?.activePlan || user?.activePlanName || 'No active membership';
  const membershipStatus = credits?.membershipStatus || user?.membershipStatus || 'inactive';
  const availableCredits = credits?.availableCredits ?? null;
  const periodEnd = credits?.membershipCurrentPeriodEnd || user?.membershipCurrentPeriodEnd || null;
  const creditsExpireAt = credits?.creditsExpireAt || null;

  return (
    <div className='mt-8 space-y-6'>
      <div className='grid gap-6 md:grid-cols-2'>
        <Info label='Current Plan' value={planName} highlight={hasActiveMembership ? 'green' : undefined} />
        <Info label='Membership Status' value={formatStatus(membershipStatus)} highlight={hasActiveMembership ? 'green' : 'red'} />
        <Info label='Available Credits' value={availableCredits === null ? 'Unavailable' : `${availableCredits} PrimeTime Credits`} />
        <Info label='Current Period Ends' value={formatAccountDate(periodEnd)} />
        <Info label='Next Credit Expiration' value={formatAccountDate(creditsExpireAt)} />
      </div>

      {!hasActiveMembership && (
        <div className='rounded-xl border border-white/10 bg-black/30 p-5 text-sm leading-6 text-white/60'>
          No active membership is currently connected to this account. Choose a membership plan to start checkout; credits will appear here automatically.
        </div>
      )}
    </div>
  );
}

function formatStatus(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatAccountDate(value: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
}

function Table({ rows, empty }: { rows: any[]; empty: string }) {
  if (!rows.length) return <p className='mt-8 text-white/45'>{empty}</p>;
  return (
    <div className='mt-8 overflow-hidden rounded-xl border border-white/10'>
      {rows.map((row, idx) => (
        <div key={idx} className='grid grid-cols-4 gap-4 border-t border-white/10 p-4 text-sm first:border-t-0'>
          {Object.values(row).slice(0, 4).map((value: any, i) => (
            <div key={i} className='text-white/70'>{String(value ?? '-')}</div>
          ))}
        </div>
      ))}
    </div>
  );
}
